import { useEffect, useState } from 'react';
import { ListChecks, ListTree, RefreshCw } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type CreateMonitoredTermInput,
  type MonitoredTerm,
  type MonitoredTermMatchType,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, TextInput } from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';
import { categoryColorClasses } from './term-category-colors';

interface FormState {
  term: string;
  matchType: MonitoredTermMatchType;
  caseSensitive: boolean;
  category: string;
  description: string;
  active: boolean;
}

const EMPTY: FormState = {
  term: '',
  matchType: 'word',
  caseSensitive: false,
  category: '',
  description: '',
  active: true,
};

const CATEGORY_SUGGESTIONS = [
  'Cliente',
  'Tipo de ação',
  'Palavra-chave',
  'Outra',
];

const MATCH_TYPE_LABEL: Record<MonitoredTermMatchType, string> = {
  word: 'Palavra',
  phrase: 'Frase',
  regex: 'Regex',
};

/**
 * J08 v1 — Termos monitorados.
 *
 * CRUD + rescan por termo + rescan-all. Soft delete preserva o historico de
 * matches no banco; aqui exibimos apenas termos com deleted_at IS NULL.
 *
 * Rotulada como "Alertas por palavra" na UI (mai/2026). Backend e tabelas
 * mantem o nome historico monitored_terms — a renomeacao e estritamente
 * de copy. O conceito mais amplo de "monitoramento" (PF/PJ por OAB, CPF
 * ou CNPJ) vive em /monitoramentos.
 */
export function MonitoredTermsPage() {
  const [items, setItems] = useState<MonitoredTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<'true' | 'false' | 'all'>(
    'all',
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MonitoredTerm | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [rescanningId, setRescanningId] = useState<string | null>(null);
  const [rescanAllLoading, setRescanAllLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  function reload() {
    setLoading(true);
    setError(null);
    apiClient
      .monitoredTermsList(filterActive)
      .then((rows) => setItems(rows))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(reload, [filterActive]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: MonitoredTerm) {
    setEditing(item);
    setForm({
      term: item.term,
      matchType: item.matchType,
      caseSensitive: item.caseSensitive,
      category: item.category ?? '',
      description: item.description ?? '',
      active: item.active,
    });
    setFormError(null);
    setModalOpen(true);
  }

  async function submit() {
    if (form.term.trim().length < 2) {
      setFormError('Termo deve ter pelo menos 2 caracteres');
      return;
    }
    if (form.matchType === 'regex') {
      try {
        new RegExp(form.term);
      } catch (e) {
        setFormError(`Regex inválida: ${(e as Error).message}`);
        return;
      }
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const payload: CreateMonitoredTermInput = {
        term: form.term.trim(),
        matchType: form.matchType,
        caseSensitive: form.caseSensitive,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        active: form.active,
      };
      if (editing) {
        await apiClient.monitoredTermsUpdate(editing.id, payload);
      } else {
        await apiClient.monitoredTermsCreate(payload);
      }
      setModalOpen(false);
      reload();
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: MonitoredTerm) {
    if (
      !window.confirm(
        `Desativar e remover "${item.term}"? O histórico de matches passados será preservado.`,
      )
    )
      return;
    try {
      await apiClient.monitoredTermsDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      setFeedback(`Termo "${item.term}" removido.`);
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  async function handleRescan(item: MonitoredTerm) {
    if (
      !window.confirm(
        `Reescanear todas as intimações existentes em busca de "${item.term}"? Pode demorar alguns segundos.`,
      )
    )
      return;
    setRescanningId(item.id);
    setFeedback(null);
    try {
      const r = await apiClient.monitoredTermsRescan(item.id);
      setFeedback(
        `"${item.term}": ${r.scanned} intimação(ões) verificada(s), ${r.newMatches} novo(s) match(es).`,
      );
      reload();
    } catch (e) {
      setFeedback(`Erro: ${(e as ApiError).message}`);
    } finally {
      setRescanningId(null);
    }
  }

  async function handleRescanAll() {
    if (
      !window.confirm(
        'Reescanear todas as intimações em busca de TODOS os termos ativos? Operação cara.',
      )
    )
      return;
    setRescanAllLoading(true);
    setFeedback(null);
    try {
      const r = await apiClient.monitoredTermsRescanAll();
      setFeedback(
        `${r.scanned} intimação(ões) verificada(s), ${r.newMatches} novo(s) match(es).`,
      );
      reload();
    } catch (e) {
      setFeedback(`Erro: ${(e as ApiError).message}`);
    } finally {
      setRescanAllLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
            Alertas por palavra
          </h1>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">
            Cadastre palavras ou expressões que serão automaticamente
            destacadas em novas intimações do DJEN. Para monitorar uma
            pessoa específica por OAB, CPF ou CNPJ, use Monitoramentos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleRescanAll}
            disabled={rescanAllLoading}
          >
            <RefreshCw
              className={
                rescanAllLoading ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'
              }
            />
            Reescanear tudo
          </Button>
          <Button onClick={openNew}>
            <ListChecks className="mr-2 h-4 w-4" />
            Novo termo
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--fg-muted)]">
        <span className="font-semibold uppercase tracking-wider">Filtrar:</span>
        {(['all', 'true', 'false'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilterActive(f)}
            className={
              filterActive === f
                ? 'rounded-md bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] px-2.5 py-1 text-[var(--fg)]'
                : 'rounded-md px-2.5 py-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]'
            }
          >
            {f === 'all' ? 'Todos' : f === 'true' ? 'Ativos' : 'Inativos'}
          </button>
        ))}
      </div>

      {feedback && (
        <div className="rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)] px-4 py-3 text-sm text-[var(--fg)]">
          {feedback}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando...
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <ListTree className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum termo cadastrado.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Termo</th>
                <th className="px-4 py-2 font-medium">Categoria</th>
                <th className="px-4 py-2 font-medium">Tipo</th>
                <th className="px-4 py-2 font-medium text-right">Matches</th>
                <th className="px-4 py-2 font-medium">Último match</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const colors = categoryColorClasses(item.category);
                return (
                  <tr
                    key={item.id}
                    className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                  >
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                      >
                        {item.term}
                      </button>
                      {item.description && (
                        <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {item.category ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors}`}
                        >
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--fg-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--fg-muted)]">
                      {MATCH_TYPE_LABEL[item.matchType]}
                      {item.caseSensitive && ' · case-sensitive'}
                    </td>
                    <td className="px-4 py-2 text-right text-[var(--fg-muted)]">
                      {item.matchesCount}
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--fg-muted)]">
                      {item.lastMatchedAt
                        ? new Date(item.lastMatchedAt).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          item.active
                            ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                            : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                        }
                      >
                        {item.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRescan(item)}
                          disabled={rescanningId !== null || !item.active}
                          title={
                            item.active
                              ? 'Re-escanear intimações existentes'
                              : 'Termo inativo'
                          }
                        >
                          <RefreshCw
                            className={
                              rescanningId === item.id
                                ? 'h-4 w-4 animate-spin'
                                : 'h-4 w-4'
                            }
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item)}
                        >
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar termo' : 'Novo termo monitorado'}
        description="Será aplicado automaticamente nas próximas intimações."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Termo" required>
            <TextInput
              value={form.term}
              onChange={(e) =>
                setForm((s) => ({ ...s, term: e.target.value }))
              }
              placeholder={
                form.matchType === 'regex'
                  ? 'Ex: penhora|arresto'
                  : 'Ex: Banco do Brasil'
              }
              maxLength={500}
            />
          </Field>
          <Field label="Tipo de match">
            <div className="flex flex-col gap-1.5 text-sm text-[var(--fg)]">
              {(['word', 'phrase', 'regex'] as const).map((t) => (
                <label key={t} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="matchType"
                    value={t}
                    checked={form.matchType === t}
                    onChange={() =>
                      setForm((s) => ({ ...s, matchType: t }))
                    }
                  />
                  <span>
                    <span className="font-medium">{MATCH_TYPE_LABEL[t]}</span>
                    <span className="ml-2 text-xs text-[var(--fg-muted)]">
                      {t === 'word' &&
                        'Casa apenas a palavra inteira (boundary).'}
                      {t === 'phrase' && 'Substring exata, ignora limites.'}
                      {t === 'regex' && 'Expressão regular ECMAScript.'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Categoria" hint="Define a cor do chip na inbox.">
            <input
              list="monitored-term-categories"
              value={form.category}
              onChange={(e) =>
                setForm((s) => ({ ...s, category: e.target.value }))
              }
              placeholder="Ex: Cliente"
              maxLength={60}
              className="h-9 w-full rounded-md border border-[var(--border)] bg-transparent px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <datalist id="monitored-term-categories">
              {CATEGORY_SUGGESTIONS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label="Descrição" hint="Opcional. Só aparece nesta tela.">
            <TextInput
              value={form.description}
              onChange={(e) =>
                setForm((s) => ({ ...s, description: e.target.value }))
              }
              placeholder="Ex: monitorar acordos do escritório X"
              maxLength={500}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.caseSensitive}
              onChange={(e) =>
                setForm((s) => ({ ...s, caseSensitive: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Diferenciar maiúsculas e minúsculas (case-sensitive)
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Ativo (aplica em novas intimações)
          </label>
        </div>
      </ConfigModal>
    </div>
  );
}
